import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { loadState } from "library/helpers/sessionStorage";

type UseUserAssignedCustomersOptions = {
  includeAllOption?: boolean;
};

type DropdownOption = {
  label: string;
  value: string;
};

function selectCustomersArray(state: RootState): any[] {
  const allCustomersVendorsCustomers: any = (state as any)?.allCustomersVendors
    ?.customers;
  if (Array.isArray(allCustomersVendorsCustomers)) {
    return allCustomersVendorsCustomers;
  }
  if (
    allCustomersVendorsCustomers?.customers &&
    Array.isArray(allCustomersVendorsCustomers.customers)
  ) {
    return allCustomersVendorsCustomers.customers;
  }

  const customersVendorsCustomers: any = (state as any)?.customersVendors
    ?.customers;
  if (Array.isArray(customersVendorsCustomers)) {
    return customersVendorsCustomers;
  }
  if (
    customersVendorsCustomers?.customers &&
    Array.isArray(customersVendorsCustomers.customers)
  ) {
    return customersVendorsCustomers.customers;
  }

  return [];
}

export const useUserAssignedCustomers = (
  options: UseUserAssignedCustomersOptions = {}
) => {
  const { includeAllOption = false } = options;
  const customers = useSelector((state: RootState) =>
    selectCustomersArray(state)
  );
  const userDetails: any = loadState("userInfo");

  const filteredCustomers = useMemo(() => {
    if (!customers || customers.length === 0) return [] as any[];
    const userAssignedCustomers: any[] = userDetails?.customers || [];
    if (!userAssignedCustomers.length) return [] as any[];

    return customers.filter((customer: any) =>
      userAssignedCustomers.some((userCustomer: any) => {
        const matchById =
          (userCustomer?.id &&
            (userCustomer.id === customer?.customerId ||
              userCustomer.id === customer?.id)) ||
          false;
        const matchByCompanyCode =
          (userCustomer?.companyCode &&
            userCustomer.companyCode === customer?.companyCode) ||
          false;
        const matchByNameOrCode =
          (userCustomer?.name &&
            (userCustomer.name === customer?.companyCode ||
              userCustomer.name === customer?.companyName)) ||
          false;
        return matchById || matchByCompanyCode || matchByNameOrCode;
      })
    );
  }, [customers, userDetails]);

  const customerDropdownOptions: DropdownOption[] = useMemo(() => {
    const base = filteredCustomers.map((customer: any) => ({
      label: customer?.companyName,
      value: customer?.companyCode,
    }));
    if (includeAllOption) {
      return [{ label: "ALL", value: "ALL" }, ...base];
    }
    return base;
  }, [filteredCustomers, includeAllOption]);

  const customerCodes: string[] = useMemo(
    () =>
      filteredCustomers
        .map((customer: any) => customer?.companyCode)
        .filter(Boolean),
    [filteredCustomers]
  );

  return {
    filteredCustomers,
    customerDropdownOptions,
    customerCodes,
  };
};

export default useUserAssignedCustomers;
